import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { HttpBackend, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { Observable } from 'rxjs';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { OAuthModule } from 'angular-oauth2-oidc';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { ApiModule } from '@nx-customer-apps/api-planning-projects';
import { ViHeaderDomainModule } from '@vi-succ/vi-header-domain';
import { environment } from '../environments/environment';
import { AuthService } from './core/services';
import { appRoutes } from './app.routes';
import { ProductsModule, AUTH_PROVIDER, authInterceptor, RoomByRoomModule, VapiModule } from '@nx-customer-apps/components';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { RoomByRoomAuthProvider } from './core/services/room-by-room-auth.provider';
import { accessTokenInterceptor, apiKeyInterceptor } from './core/interceptors';

function HttpLoaderFactory(http: HttpBackend) {
    return new MultiTranslateHttpLoader(http, [
        {
            prefix: `${environment.translationsBaseUrl}/projects/${environment.phraseappProjectId.questionFlow}/download?locale=`,
            suffix: '&format=simple_json'
        },
        {
            prefix: `${environment.translationsBaseUrl}/projects/${environment.phraseappProjectId.designSystemWeb}/download?locale=`,
            suffix: '&format=simple_json'
        },
        {
            prefix: `${environment.translationsBaseUrl}/projects/${environment.phraseappProjectId.planningToolsConfig}/download?locale=`,
            suffix: '&format=simple_json'
        },
        {
            prefix: `${environment.translationsBaseUrl}/projects/${environment.phraseappProjectId.components}/download?locale=`,
            suffix: '&format=simple_json'
        }
    ]);
}

export function authAppInitializerFactory(authService: AuthService): () => Observable<void> {
    return () => authService.runInitialLoginSequence();
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(appRoutes),
        provideHttpClient(withInterceptors([accessTokenInterceptor, apiKeyInterceptor, authInterceptor])),
        provideAnimationsAsync(),
        importProvidersFrom(
            OAuthModule.forRoot(),
            ApiModule.forRoot({ rootUrl: environment.baseUrl }),
            VapiModule.forRoot({
                baseUrl: environment.vapi.baseUrl,
                entrypoint: environment.vapi.entrypoint,
                version: environment.vapi.version
            }),
            ViHeaderDomainModule.forRoot({
                baseUrl: environment.baseUrl
            }),
            ProductsModule.forRoot(environment.apiPlanningProjectsConfig),
            RoomByRoomModule.forRoot(environment.apiPlanningProjectsConfig),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpBackend]
                }
            }),
            ServiceWorkerModule.register('ngsw-worker.js', {
                enabled: environment.serviceWorker,
                registrationStrategy: 'registerWhenStable:30000'
            })
        ),
        provideAppInitializer(() => {
            const authService = inject(AuthService);
            const initializerFn = authAppInitializerFactory(authService);
            return initializerFn();
        }),
        {
            provide: MAT_RIPPLE_GLOBAL_OPTIONS,
            useValue: { disabled: true }
        },
        { provide: AUTH_PROVIDER, useClass: RoomByRoomAuthProvider }
    ]
};
